import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Film,
  CheckCircle2,
  AlertCircle,
  Lock,
} from "lucide-react";

const ScriptForm = ({
  title,
  setTitle,
  currentScene,
  setCurrentScene,
  sceneTitles,
  sceneContents,
  setSceneTitle,
  setSceneContent,
  selectedFont,
  selectedColor,
  backgroundOption,
  backgroundOptions,
}) => {
  const maxScenes = 10; // 10 scenes
  const maxSceneLength = 5000; // Updated: 5000 chars (same as chapters)
  const minSceneLength = 50; // Minimum 50 chars

  // Check if a specific scene is complete
  const isSceneComplete = (sceneIndex) => {
    return (
      sceneTitles[sceneIndex]?.trim() &&
      sceneContents[sceneIndex]?.trim() &&
      sceneContents[sceneIndex].trim().length >= minSceneLength
    );
  };

  // Check if current scene is complete
  const isCurrentSceneComplete = () => {
    return isSceneComplete(currentScene - 1);
  };

  // Check if a scene is unlocked
  const isSceneUnlocked = (sceneNum) => {
    if (sceneNum === 1) return true;
    
    for (let i = 0; i < sceneNum - 1; i++) {
      if (!isSceneComplete(i)) {
        return false;
      }
    }
    return true;
  };

  // Navigate to previous scene
  const goToPreviousScene = () => {
    if (currentScene > 1) {
      setCurrentScene(currentScene - 1);
    }
  };

  // Navigate to next scene
  const goToNextScene = () => {
    if (currentScene < maxScenes && isSceneUnlocked(currentScene + 1)) {
      setCurrentScene(currentScene + 1);
    }
  };

  const currentSceneIndex = currentScene - 1;
  const currentSceneTitle = sceneTitles[currentSceneIndex] || "";
  const currentSceneContent = sceneContents[currentSceneIndex] || "";
  const remainingChars = maxSceneLength - currentSceneContent.length;
  const isNextSceneUnlocked = isSceneUnlocked(currentScene + 1);

  return (
    <div className="space-y-6">
      {/* Script Title */}
      <div>
        <div className="sm:hidden">
          <div className="relative">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter your script title..."
              className="w-full p-4 text-base bg-transparent border-0 border-b-2 border-black focus:outline-none focus:ring-0 placeholder-gray-500"
              maxLength={30}
            />
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"></div>
          </div>
          <div className="text-xs text-gray-500 mt-2 px-1">
            {title.length}/30 characters
          </div>
        </div>
        
        <div className="hidden sm:block">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Script Title *
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter your script title..."
            className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition shadow-sm"
            maxLength={30}
          />
          <div className="text-xs text-gray-500 mt-1">
            {title.length}/30 characters
          </div>
        </div>
      </div>

      {/* Scene Progress Bar */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
            <Film size={16} />
            Scene Progress
          </h3>
          <span className="text-xs text-gray-500">
            {Array.from({ length: maxScenes }, (_, i) => i).filter(i => isSceneComplete(i)).length}/{maxScenes} completed
          </span>
        </div>
        
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => {
            const isUnlocked = isSceneUnlocked(num);
            const isComplete = isSceneComplete(num - 1);
            const isCurrent = currentScene === num;
            
            return (
              <button
                key={num}
                onClick={() => isUnlocked && setCurrentScene(num)}
                disabled={!isUnlocked}
                className={`relative h-10 rounded-lg border-2 transition-all flex items-center justify-center text-sm font-medium ${
                  isCurrent
                    ? "border-gray-900 bg-gray-900 text-white shadow-md"
                    : isComplete
                    ? "border-green-500 bg-green-50 text-green-700 hover:bg-green-100"
                    : isUnlocked
                    ? "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100"
                    : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                }`}
                title={!isUnlocked ? "Complete previous scenes to unlock" : `Scene ${num}`}
              >
                {!isUnlocked ? (
                  <Lock size={14} />
                ) : isComplete ? (
                  <CheckCircle2 size={16} />
                ) : (
                  num
                )}
              </button>
            );
          })}
        </div>
        
        <div className="mt-3 flex items-start gap-2 p-2 bg-blue-50 rounded-lg">
          <AlertCircle size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-blue-700">
            Complete each scene (title + min 50 chars) to unlock the next one
          </p>
        </div>
      </div>

      {/* Scene Navigation */}
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg border border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={goToPreviousScene}
            disabled={currentScene === 1}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={16} />
            <span className="text-sm font-medium hidden sm:inline">Previous</span>
          </button>

          <div className="flex flex-col items-center">
            <span className="text-xs text-gray-500 mb-1">Currently Editing</span>
            <span className="text-lg font-bold text-gray-900">
              Scene {currentScene}
            </span>
          </div>

          <button
            type="button"
            onClick={goToNextScene}
            disabled={currentScene === maxScenes || !isNextSceneUnlocked}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            title={!isNextSceneUnlocked ? "Complete current scene to unlock next" : ""}
          >
            <span className="text-sm font-medium hidden sm:inline">Next</span>
            {!isNextSceneUnlocked && currentScene < maxScenes ? (
              <Lock size={16} />
            ) : (
              <ChevronRight size={16} />
            )}
          </button>
        </div>

        {!isCurrentSceneComplete() && (
          <div className="mt-3 p-2 bg-yellow-50 rounded-lg border border-yellow-200">
            <p className="text-xs text-yellow-700 text-center">
              Complete this scene to unlock Scene {currentScene + 1}
            </p>
          </div>
        )}
      </div>

      {/* Scene Title */}
      <div>
        <div className="sm:hidden">
          <div className="relative">
            <input
              type="text"
              value={currentSceneTitle}
              onChange={(e) => setSceneTitle(currentSceneIndex, e.target.value)}
              placeholder={`Scene ${currentScene} title...`}
              className="w-full p-4 text-base bg-transparent border-0 border-b-2 border-black focus:outline-none focus:ring-0 placeholder-gray-500"
              maxLength={30}
            />
            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-black"></div>
          </div>
          <div className="text-xs text-gray-500 mt-2 px-1">
            {currentSceneTitle.length}/30 characters
          </div>
        </div>
        
        <div className="hidden sm:block">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Scene {currentScene} Title *
          </label>
          <input
            type="text"
            value={currentSceneTitle}
            onChange={(e) => setSceneTitle(currentSceneIndex, e.target.value)}
            placeholder={`Enter title for Scene ${currentScene}...`}
            className="w-full p-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent transition shadow-sm"
            maxLength={30}
          />
          <div className="text-xs text-gray-500 mt-1">
            {currentSceneTitle.length}/30 characters
          </div>
        </div>
      </div>

      {/* Scene Content */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">
            Scene {currentScene} Content *
          </label>
          <div className="text-xs flex items-center gap-2">
            <span
              className={`${
                remainingChars < 500 ? "text-red-500" : "text-gray-500"
              }`}
            >
              {currentSceneContent.length.toLocaleString()}/{maxSceneLength.toLocaleString()}
            </span>
            {currentSceneContent.length < minSceneLength && (
              <span className="text-yellow-600">
                (min: {minSceneLength})
              </span>
            )}
          </div>
        </div>

        <div className="sm:hidden">
          <div
            className={`relative border-t-2 border-b-2 border-black overflow-hidden ${
              backgroundOptions[backgroundOption]?.class || "bg-white"
            }`}
          >
            <textarea
              value={currentSceneContent}
              onChange={(e) => setSceneContent(currentSceneIndex, e.target.value)}
              placeholder={`Write Scene ${currentScene} content here...\n\nNo character limit per line for scripts. Write freely!\n\nMinimum ${minSceneLength} characters required.`}
              className={`w-full p-2 text-[14px] bg-transparent focus:outline-none min-h-[300px] resize-y ${selectedFont} ${
                selectedColor === 'white' ? 'text-white' : 'text-black'
              } placeholder-gray-500`}
              maxLength={maxSceneLength}
            />
          </div>
        </div>
        
        <div className="hidden sm:block">
          <div
            className={`rounded-lg overflow-hidden border-2 border-gray-300 focus-within:border-gray-800 transition-colors ${
              backgroundOptions[backgroundOption]?.class || "bg-white"
            }`}
          >
            <textarea
              value={currentSceneContent}
              onChange={(e) => setSceneContent(currentSceneIndex, e.target.value)}
              placeholder={`Write Scene ${currentScene} content here...\n\nNo character limit per line for scripts. Write freely!\n\nMinimum ${minSceneLength} characters required.`}
              className={`w-full p-4 bg-transparent focus:outline-none min-h-[400px] resize-y ${selectedFont} ${
                selectedColor === 'white' ? 'text-white' : 'text-black'
              } placeholder-gray-400 text-base`}
              maxLength={maxSceneLength}
            />
          </div>
        </div>

        {/* Character count warnings */}
        {currentSceneContent.length < minSceneLength && currentSceneContent.length > 0 && (
          <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-2">
            <AlertCircle size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-yellow-700">
              <strong>{minSceneLength - currentSceneContent.length} more characters</strong> needed to complete this scene
            </p>
          </div>
        )}

        {/* Completion indicator */}
        {isCurrentSceneComplete() && (
          <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
            <CheckCircle2 size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-green-700">
              <strong>Scene {currentScene} completed!</strong> 
              {currentScene < maxScenes ? " You can now move to the next scene." : " This is your final scene."}
            </p>
          </div>
        )}
      </div>

      {/* Guidelines */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <div className="flex items-start gap-2">
          <Film size={16} className="text-gray-600 mt-0.5 flex-shrink-0" />
          <div className="text-xs text-gray-700">
            <p className="font-medium mb-2">Script Writing Guidelines:</p>
            <ul className="space-y-1 list-disc list-inside text-gray-600">
              <li>Add up to 10 scenes to your script</li>
              <li>Each scene: 50 - 5,000 characters</li>
              <li>Complete scenes sequentially to unlock next</li>
              <li>No line length restrictions - write freely!</li>
              <li>Scenes must have both title and content</li>
              <li>Perfect for screenplays, stage plays, and narratives</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScriptForm;